import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { ChevronUp } from 'lucide-react';
import { PrivacySEO } from '../components/SEO';

const content = {
  en: {
    title: 'Privacy & Cookies Policy',
    sections: [
      {
        heading: 'About This Policy',
        body: 'This privacy and cookies policy explains how we use cookies and personal data on our digital menu service.'
      },
      {
        heading: 'Essential Cookies',
        body: 'We use essential cookies for website functionality, including:\n• Language preference (menu_language) - saves your selected language for 365 days\n• Cookie consent choice (cookie_consent) - remembers your privacy preferences\n\nThese cookies do not track you and are necessary for the site to work properly.'
      },
      {
        heading: 'Analytics Cookies (Optional)',
        body: 'With your consent, we may use Google Analytics to understand how visitors use our digital menu. This helps us improve the user experience. Analytics cookies:\n• Are only loaded if you accept\n• Collect anonymous usage data\n• Help us understand which menu items are popular\n• Can be disabled at any time by clearing your cookie consent\n\nNo personal data is collected without your explicit consent.'
      },
      {
        heading: 'Your Privacy Rights',
        body: 'You have the right to:\n• Accept or reject analytics cookies\n• Clear your cookies at any time\n• Request deletion of your data\n• Opt out of analytics tracking\n\nCookie preferences can be changed by clearing your browser cookies for this site.'
      },
      {
        heading: 'Data Security',
        body: 'We are committed to protecting your data. Our servers use HTTPS encryption to secure all communications. No credit card or payment information is stored on our servers.'
      },
      {
        heading: 'Contact Us',
        body: 'If you have questions about this policy or our privacy practices, please contact the restaurant owner through the contact information on their website.'
      }
    ]
  },
  tr: {
    title: 'Gizlilik & Çerezler Politikası',
    sections: [
      {
        heading: 'Bu Politika Hakkında',
        body: 'Bu gizlilik ve çerezler politikası, dijital menü hizmetimizde çerezleri ve kişisel verileri nasıl kullandığımızı açıklar.'
      },
      {
        heading: 'Gerekli Çerezler',
        body: 'Website işlevselliği için gerekli çerezleri kullanırız:\n• Dil tercihi (menu_language) - seçtiğiniz dili 365 gün boyunca kaydeder\n• Çerez onayı (cookie_consent) - gizlilik tercihlerinizi hatırlar\n\nBu çerezler sizi takip etmez ve sitenin düzgün çalışması için gereklidir.'
      },
      {
        heading: 'Analitik Çerezler (İsteğe Bağlı)',
        body: 'Onayınız ile, ziyaretçilerin dijital menüyü nasıl kullandığını anlamak için Google Analytics kullanabiliriz. Bu, kullanıcı deneyimini geliştirmemize yardımcı olur. Analitik çerezleri:\n• Yalnızca onay verirseniz yüklenir\n• Anonim kullanım verilerini toplar\n• Hangi menü öğelerinin popüler olduğunu anlamaya yardımcı olur\n• Çerez onayınızı temizleyerek istediğiniz zaman devre dışı bırakılabilir\n\nAçık onayınız olmadan hiçbir kişisel veri toplanmaz.'
      },
      {
        heading: 'Gizlilik Haklarınız',
        body: 'Şu haklara sahipsiniz:\n• Analitik çerezleri kabul etme veya reddetme\n• İstediğiniz zaman çerezleri temizleme\n• Verilerinizin silinmesini isteme\n• Analitik izlemeyi devre dışı bırakma\n\nÇerez tercihleri, bu site için tarayıcı çerezlerinizi temizleyerek değiştirilebilir.'
      },
      {
        heading: 'Veri Güvenliği',
        body: 'Verilerinizi korumaya kararlıyız. Sunucularımız tüm iletişimleri güvenleştirmek için HTTPS şifrelemesi kullanır. Hiçbir kredi kartı veya ödeme bilgisi sunucularımızda depolanmaz.'
      },
      {
        heading: 'İletişim',
        body: 'Bu politika hakkında veya gizlilik uygulamalarımız hakkında sorularınız varsa, lütfen işletmenizin web sitesindeki iletişim bilgileri aracılığıyla işletmenin sahibiyle iletişime geçin.'
      }
    ]
  }
};

export default function Privacy() {
  const [language, setLanguage] = useState(() => {
    // Detect language from menu_language cookie (set by PublicMenu)
    const savedLang = Cookies.get('menu_language');
    return (savedLang === 'tr' || savedLang === 'en') ? savedLang : 'en';
  });
  const t = content[language];

  return (
    <>
    <PrivacySEO />
    <div style={styles.container}>
      <header style={styles.header}>
        <h1>{t.title}</h1>
        <div style={styles.langSelector}>
          <button 
            onClick={() => setLanguage('en')}
            style={{ ...styles.langBtn, ...(language === 'en' ? styles.langBtnActive : {}) }}
          >
            English
          </button>
          <button 
            onClick={() => setLanguage('tr')}
            style={{ ...styles.langBtn, ...(language === 'tr' ? styles.langBtnActive : {}) }}
          >
            Türkçe
          </button>
        </div>
      </header>

      <main style={styles.content}>
        {t.sections.map((section, idx) => (
          <section key={idx} style={styles.section}>
            <h2 style={styles.sectionHeading}>{section.heading}</h2>
            <p style={styles.sectionBody}>
              {section.body.split('\n').map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  {i < section.body.split('\n').length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>
          </section>
        ))}
      </main>

      <footer style={styles.footer}>
        <p>Last updated: December 2025</p>
      </footer>
    </div>
    </>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f5f5f5',
    padding: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    maxWidth: '800px',
    margin: '0 auto 40px',
    textAlign: 'center',
  },
  langSelector: {
    marginTop: '16px',
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
  },
  langBtn: {
    padding: '8px 16px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    background: '#f0f0f0',
    color: '#333',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.2s ease',
  },
  langBtnActive: {
    background: '#2196f3',
    color: '#fff',
    borderColor: '#2196f3',
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
    background: '#fff',
    borderRadius: '8px',
    padding: '32px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  section: {
    marginBottom: '28px',
    paddingBottom: '20px',
    borderBottom: '1px solid #eee',
  },
  sectionHeading: {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '12px',
    color: '#333',
  },
  sectionBody: {
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#555',
    whiteSpace: 'pre-wrap',
  },
  footer: {
    maxWidth: '800px',
    margin: '32px auto 0',
    textAlign: 'center',
    fontSize: '14px',
    color: '#777',
    paddingTop: '20px',
    borderTop: '1px solid #eee',
  },
  backLink: {
    display: 'inline-block',
    marginTop: '12px',
    color: '#2196f3',
    textDecoration: 'none',
  }
};
